import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithChildren,
  PropsWithoutRef,
  ReactNode,
  RefAttributes,
} from 'react';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type CloseReason = 'backdropClick' | 'escapeKeyDown';

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const DEFAULT_SIZE: ModalSize = 'sm';

const BACKDROP_SX = {
  backgroundColor: 'rgba(0, 0, 0, 0.32)',
};

const PAPER_SX = {
  borderRadius: 2,
  backgroundColor: '#ffffff',
};

const CLOSE_BUTTON_SX = {
  position: 'absolute',
  top: 12,
  right: 12,
  color: '#49454f',
};

export type Props = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  size?: ModalSize;
  fullWidth?: boolean;
  dividers?: boolean;
  showCloseButton?: boolean;
  disableBackdropClose?: boolean;
  tid?: string;
  contentTid?: string;
  titleTid?: string;
  actionsTid?: string;
  closeTid?: string;
} & PropsWithChildren;

export const Modal: FR<HTMLDivElement, Props> = forwardRef((props: Props, ref) => {
  const {
    open,
    onClose,
    title,
    description,
    actions,
    size = DEFAULT_SIZE,
    fullWidth = false,
    dividers = false,
    showCloseButton = true,
    disableBackdropClose = false,
    children,
    tid,
    contentTid,
    titleTid,
    actionsTid,
    closeTid,
  } = props;

  const hasHeader = title !== undefined || showCloseButton;
  const hasContent = children !== undefined || description !== undefined;

  const handleClose = (_event: object, reason: CloseReason): void => {
    if (disableBackdropClose && reason === 'backdropClick') return;

    onClose();
  };

  return (
    <Dialog
      ref={ref}
      // [SECTION] State
      open={open}
      onClose={handleClose}
      // [SECTION] Sizing
      maxWidth={size}
      fullWidth={fullWidth}
      scroll="paper"
      // [SECTION] Style
      data-testid={tid}
      slotProps={{
        backdrop: { sx: BACKDROP_SX },
        paper: { sx: PAPER_SX },
      }}
    >
      {/* [SECTION] Header */}
      {hasHeader && (
        <DialogTitle
          data-testid={titleTid}
          sx={{
            pr: showCloseButton ? 6 : 3,
            fontWeight: 700,
            color: '#49454f',
          }}
        >
          {title}
        </DialogTitle>
      )}

      {showCloseButton && (
        <IconButton aria-label="Cerrar" onClick={onClose} data-testid={closeTid} sx={CLOSE_BUTTON_SX}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      )}

      {/* [SECTION] Content */}
      {hasContent && (
        <DialogContent dividers={dividers} data-testid={contentTid}>
          {description !== undefined && (
            <DialogContentText sx={{ mb: children !== undefined ? 2 : 0, color: '#6b7280' }}>
              {description}
            </DialogContentText>
          )}

          {children}
        </DialogContent>
      )}

      {/* [SECTION] Actions */}
      {actions !== undefined && (
        <DialogActions data-testid={actionsTid} sx={{ p: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>{actions}</Box>
        </DialogActions>
      )}
    </Dialog>
  );
});
